import {
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { DateTimeUtility } from '../utility/date-time.utility';
import { StringUtility } from '../utility/string.utility';
import { OptionalDateRangeValidator } from '../validators/optional-date-range.validator';
import { BaseResponse } from './base-response.model';
import { Validation } from './validation.model';

export interface HousingUnitResponse extends BaseResponse {
	message: string;
	housingUnits: HousingUnitJSON[];
}

export interface AddHousingUnitResponse extends BaseResponse {
	message: string;
	housingUnit: HousingUnitJSON;
}

export interface GetHousingUnitResponse extends BaseResponse {
	message: string;
	housingUnit: HousingUnitJSON;
}

export interface HousingUnitJSON {
	id: number;
	label: string;
	unit: string;
	compound: string;
	unitType: string;
	numberOfBedrooms: number;
	allowedPets: string;
	contractStartDate: string;
	contractEndDate: string;
	monthlyRent: number;
	kahramaaId: string;
	currentTenant: string;
	phoneNumber: string;
	formerTenants: string;
	notes: string;
	cgisUnit: string;
	cgisBuildingNumber: string;
	cgisStreet: string;
	cgisZone: string;
	archived: boolean;
	archivedOn: string;
	archivedUserId: number;
}

export class HousingUnit {
	constructor(
		public id: number,
		public label: string,
		public unit: string,
		public compound: string,
		public unitType: string,
		public numberOfBedrooms: number,
		public allowedPets: string,
		public contractStartDate: Date,
		public contractEndDate: Date,
		public monthlyRent: number,
		public kahramaaId: string,
		public currentTenant: string,
		public phoneNumber: string,
		public formerTenants: string,
		public notes: string,
		public cgisUnit: string,
		public cgisBuildingNumber: string,
		public cgisStreet: string,
		public cgisZone: string,
		public archived: boolean,
		public archivedOn: Date,
		public archivedUserId: number
	) {}

	static fromJSON(json: HousingUnitJSON): HousingUnit {
		return Object.assign(Object.create(HousingUnit.prototype), json, {
			contractStartDate: json.contractStartDate
				? DateTimeUtility.fromDatabaseDate(json.contractStartDate)
				: null,
			contractEndDate: json.contractEndDate
				? DateTimeUtility.fromDatabaseDate(json.contractEndDate)
				: null,
			archivedOn: json.archivedOn
				? DateTimeUtility.fromDatabaseDatetime(json.archivedOn)
				: null,
		});
	}

	static createFormGroup(
		h: HousingUnit,
		validation: Validation
	): UntypedFormGroup {
		const fg = new UntypedFormGroup({
			id: new UntypedFormControl(h ? h.id : null),
			unit: new UntypedFormControl(h ? h.unit : null, [
				Validators.required,
				Validators.maxLength(validation.alnum.maxLength),
				Validators.pattern(validation.alnum.pattern),
			]),
			compound: new UntypedFormControl(h ? h.compound : null, [
				Validators.required,
			]),
			unitType: new UntypedFormControl(h ? h.unitType : null, [
				Validators.required,
			]),
			numberOfBedrooms: new UntypedFormControl(
				h ? h.numberOfBedrooms : null,
				[
					Validators.maxLength(validation.number.maxLength),
					Validators.pattern(validation.number.pattern),
				]
			),
			allowedPets: new UntypedFormControl(h ? h.allowedPets : null),
			phoneNumber: new UntypedFormControl(
				h
					? StringUtility.ensureFirstCharacterIsPlus(h.phoneNumber)
					: '',
				[
					Validators.maxLength(validation.phone.maxLength),
					Validators.pattern(validation.phone.pattern),
				]
			),
			formerTenants: new UntypedFormControl(h ? h.formerTenants : '', [
				Validators.maxLength(validation.comment.maxLength),
				Validators.pattern(validation.comment.pattern),
			]),
			notes: new UntypedFormControl(h ? h.notes : '', [
				Validators.maxLength(validation.comment.maxLength),
				Validators.pattern(validation.comment.pattern),
			]),
			contractStartDate: new UntypedFormControl(
				h ? h.contractStartDate : null
			),
			contractEndDate: new UntypedFormControl(
				h ? h.contractEndDate : null
			),
			monthlyRent: new UntypedFormControl(h ? h.monthlyRent : null, [
				Validators.maxLength(validation.currency.maxLength),
				Validators.pattern(validation.currency.pattern),
			]),
			kahramaaId: new UntypedFormControl(h ? h.kahramaaId : null, [
				Validators.maxLength(validation.alnum.maxLength),
				Validators.pattern(validation.alnum.pattern),
			]),
			cgisUnit: new UntypedFormControl(h ? h.cgisUnit : null, [
				Validators.maxLength(validation.alnum.maxLength),
				Validators.pattern(validation.alnum.pattern),
			]),
			cgisBuildingNumber: new UntypedFormControl(
				h ? h.cgisBuildingNumber : null,
				[
					Validators.maxLength(validation.alnum.maxLength),
					Validators.pattern(validation.alnum.pattern),
				]
			),
			cgisStreet: new UntypedFormControl(h ? h.cgisStreet : null, [
				Validators.maxLength(validation.alnum.maxLength),
				Validators.pattern(validation.alnum.pattern),
			]),
			cgisZone: new UntypedFormControl(h ? h.cgisZone : null, [
				Validators.maxLength(validation.alnum.maxLength),
				Validators.pattern(validation.alnum.pattern),
			]),
		});
		fg.setValidators([
			OptionalDateRangeValidator(
				fg.get('contractStartDate'),
				fg.get('contractEndDate')
			),
		]);
		StringUtility.ensureFormControlFirstCharacterIsPlus(
			fg.get('phoneNumber') as UntypedFormControl
		);
		return fg;
	}

	static fromFormGroup(fg: UntypedFormGroup): HousingUnit {
		return Object.assign(Object.create(HousingUnit.prototype), {
			id: fg.get('id').value,
			unit: fg.get('unit').value,
			compound: fg.get('compound').value,
			unitType: fg.get('unitType').value,
			numberOfBedrooms: fg.get('numberOfBedrooms').value,
			allowedPets: fg.get('allowedPets').value,
			phoneNumber: fg.get('phoneNumber').value,
			formerTenants: fg.get('formerTenants').value,
			notes: fg.get('notes').value,
			contractStartDate: DateTimeUtility.toDatabaseDateString(
				fg.get('contractStartDate').value
			),
			contractEndDate: DateTimeUtility.toDatabaseDateString(
				fg.get('contractEndDate').value
			),
			monthlyRent: fg.get('monthlyRent').value,
			kahramaaId: fg.get('kahramaaId').value,
			cgisUnit: fg.get('cgisUnit').value,
			cgisBuildingNumber: fg.get('cgisBuildingNumber').value,
			cgisStreet: fg.get('cgisStreet').value,
			cgisZone: fg.get('cgisZone').value,
		});
	}

	monthlyRentString(): string {
		return this.monthlyRent ? `$${this.monthlyRent.toFixed(2)}` : '-';
	}
}
